/**
 * ValidationPanel Component - Display network validation results
 */
import React, { useEffect, useState } from 'react';
import { useProjectStore } from '../../stores';
import axios from 'axios';
import '../Dialogs/Dialog.css';

// Get API URL from environment
const API_URL = process.env.REACT_APP_API_URL && process.env.REACT_APP_API_URL !== '' ? process.env.REACT_APP_API_URL : '/api';

// Helper function to get auth token
const getAuthToken = (): string | null => {
  const authStorage = localStorage.getItem('auth-storage');
  if (authStorage) {
    try {
      const { state } = JSON.parse(authStorage);
      return state?.token || null;
    } catch (error) {
      console.error('Error parsing auth storage:', error);
      return null;
    }
  }
  return null;
};

interface ValidationResult {
  is_valid: boolean;
  errors: string[];
  warnings: string[];
  info: string[];
}

export const ValidationPanel: React.FC = () => {
  const { currentProject } = useProjectStore();
  const [validation, setValidation] = useState<ValidationResult | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (currentProject) {
      validateNetwork();
    }
  }, [currentProject]);

  const validateNetwork = async () => {
    if (!currentProject) return;

    setLoading(true);
    try {
      const token = getAuthToken();
      const response = await axios.get(
        `${API_URL}/projects/${currentProject.id}/validation/network`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        }
      );
      setValidation(response.data);
    } catch (error) {
      console.error('Failed to validate network:', error);
    } finally {
      setLoading(false);
    }
  };

  if (!currentProject) {
    return (
      <div className="validation-results">
        <h3>Validácia siete</h3>
        <p style={{ color: '#999', fontSize: '14px' }}>Nie je vybraný projekt</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="validation-results">
        <h3>Validácia siete</h3>
        <p style={{ color: '#999', fontSize: '14px' }}>Validuje sa...</p>
      </div>
    );
  }

  if (!validation) {
    return (
      <div className="validation-results">
        <h3>Validácia siete</h3>
        <p style={{ color: '#999', fontSize: '14px' }}>Žiadne validačné dáta</p>
      </div>
    );
  }

  return (
    <div className="validation-results">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
        <h3 style={{ margin: 0 }}>Validácia siete</h3>
        <button
          onClick={validateNetwork}
          style={{
            background: 'transparent',
            border: 'none',
            cursor: 'pointer',
            fontSize: '18px',
            padding: '4px 8px',
          }}
          title="Obnoviť"
        >
          🔄
        </button>
      </div>

      {validation.is_valid && (validation.errors || []).length === 0 && (validation.warnings || []).length === 0 && (
        <div className="validation-item success">
          <span className="validation-icon">✓</span>
          <span className="validation-message">Sieť je v poriadku</span>
        </div>
      )}

      {(validation.errors || []).map((error, index) => (
        <div key={`error-${index}`} className="validation-item error">
          <span className="validation-icon">✕</span>
          <span className="validation-message">{error}</span>
        </div>
      ))}

      {(validation.warnings || []).map((warning, index) => (
        <div key={`warning-${index}`} className="validation-item warning">
          <span className="validation-icon">⚠</span>
          <span className="validation-message">{warning}</span>
        </div>
      ))}

      {(validation.info || []).map((info, index) => (
        <div key={`info-${index}`} className="validation-item success">
          <span className="validation-icon">ℹ</span>
          <span className="validation-message">{info}</span>
        </div>
      ))}
    </div>
  );
};

